"use client";

import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";
import { ChevronRightIcon } from "lucide-react";
import type * as React from "react";

import { cn } from "../cn";
import { FOCUS_RING_INSET } from "./focus-ring";
import { HOVER_EASE } from "./states";

// Design spec §6.3: Work OS's 36 px work row in DTP tones. The title is the row's one link or
// button, stretched over the row; leads, chips and actions sit above it. Focus draws an inset
// ring on the whole row; hover is --sunken, selected --primary-soft, dragging 40 % opacity.

/** The row outline when its title (the stretched link) has keyboard focus. */
const ROW_FOCUS =
  "has-[[data-slot=list-row-title]:focus-visible]:outline-2 has-[[data-slot=list-row-title]:focus-visible]:outline-solid has-[[data-slot=list-row-title]:focus-visible]:-outline-offset-2 has-[[data-slot=list-row-title]:focus-visible]:outline-ring";

export type ListRowProps = Omit<React.ComponentProps<"div">, "title"> & {
  /** 16 px slot: status glyph or checkbox. */
  lead?: React.ReactNode;
  title: React.ReactNode;
  href?: string;
  onSelect?: () => void;
  /** Router link element for the title (e.g. `<Link to=… />`); takes precedence over `href`. */
  render?: React.ReactElement;
  /** `Badge`s after the title. */
  chips?: React.ReactNode;
  /** 12 px tabular meta (dates, counts). */
  meta?: React.ReactNode;
  /** Always-visible trailing items: `AvatarStack xs`, due date, priority. */
  trailing?: React.ReactNode;
  /** Row actions ("…"): shown on hover or focus within, always on coarse pointers. */
  actions?: React.ReactNode;
  selected?: boolean;
  done?: boolean;
  dragging?: boolean;
};

export function ListRow({
  lead,
  title,
  href,
  onSelect,
  render,
  chips,
  meta,
  trailing,
  actions,
  selected = false,
  done = false,
  dragging = false,
  className,
  ...props
}: ListRowProps) {
  const interactive = Boolean(render || href || onSelect);
  const titleClass = cn(
    "min-w-0 truncate text-left text-base text-foreground outline-none group-data-done/row:text-muted-foreground group-data-done/row:line-through",
    interactive && "cursor-pointer after:absolute after:inset-0 after:content-['']",
  );
  const titleEl = useRender({
    defaultTagName: href || render ? "a" : onSelect ? "button" : "span",
    render,
    props: mergeProps<"a">(
      {
        className: titleClass,
        ...(href ? { href } : {}),
        ...(onSelect ? { onClick: onSelect } : {}),
        ...(!href && !render && onSelect ? ({ type: "button" } as object) : {}),
        ...(selected && interactive ? { "aria-current": "true" as const } : {}),
      },
      { children: title },
    ),
    state: { slot: "list-row-title" },
  });
  return (
    <div
      data-slot="list-row"
      data-selected={selected || undefined}
      data-done={done || undefined}
      data-dragging={dragging || undefined}
      className={cn(
        "group/row relative flex h-9 min-w-0 items-center gap-2.5 border-b border-subtle bg-card px-3 pointer-coarse:h-11 hover:bg-sunken data-dragging:opacity-40 data-selected:bg-primary-soft",
        HOVER_EASE,
        ROW_FOCUS,
        className,
      )}
      {...props}
    >
      {lead !== undefined ? (
        <span className="relative z-10 flex size-4 shrink-0 items-center justify-center text-faint [&_svg]:size-4">
          {lead}
        </span>
      ) : null}
      <div className="flex min-w-0 flex-1 items-center gap-2">
        {titleEl}
        {chips ? (
          <span className="relative z-10 flex shrink-0 items-center gap-1">{chips}</span>
        ) : null}
      </div>
      {meta || trailing || actions ? (
        <div className="flex shrink-0 items-center gap-3 text-xs text-muted-foreground tabular-nums group-data-selected/row:text-secondary">
          {meta ? <span className="max-sm:hidden">{meta}</span> : null}
          {trailing ? (
            <span className="relative z-10 flex items-center gap-3">{trailing}</span>
          ) : null}
          {actions ? (
            <span className="relative z-10 flex items-center opacity-0 transition-opacity duration-(--dur-fast) group-hover/row:opacity-100 group-focus-within/row:opacity-100 pointer-coarse:opacity-100">
              {actions}
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export type GroupHeaderProps = Omit<React.ComponentProps<"div">, "children"> & {
  label: React.ReactNode;
  count?: number;
  /** With `onOpenChange`, the label is a disclosure button with a rotating chevron. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Controls after the count (rename, delete…), shown on the header's hover or focus. */
  actions?: React.ReactNode;
};

/** Sticky 32 px group caption on the canvas: 12 / 500 secondary, tabular muted count. */
export function GroupHeader({
  label,
  count,
  open = true,
  onOpenChange,
  actions,
  className,
  ...props
}: GroupHeaderProps) {
  return (
    <div
      data-slot="group-header"
      className={cn(
        "group/header sticky top-0 z-sticky flex h-8 items-center gap-1.5 bg-background px-2 text-xs font-medium text-secondary",
        className,
      )}
      {...props}
    >
      {onOpenChange ? (
        <button
          type="button"
          aria-expanded={open}
          onClick={() => onOpenChange(!open)}
          className={cn(
            "-ml-1 flex items-center gap-1 rounded-xs px-1 hover:text-foreground",
            HOVER_EASE,
            FOCUS_RING_INSET,
          )}
        >
          <ChevronRightIcon
            aria-hidden={true}
            className={cn(
              "size-3.5 text-faint transition-transform duration-(--dur-base) ease-out",
              open && "rotate-90",
            )}
          />
          {label}
        </button>
      ) : (
        <span>{label}</span>
      )}
      {count !== undefined ? (
        <span className="text-muted-foreground tabular-nums">{count}</span>
      ) : null}
      {actions ? (
        <span
          data-slot="group-header-actions"
          className="flex items-center gap-0.5 opacity-0 group-hover/header:opacity-100 group-focus-within/header:opacity-100 pointer-coarse:opacity-100"
        >
          {actions}
        </span>
      ) : null}
    </div>
  );
}

type IconType = React.ComponentType<{ className?: string; "aria-hidden"?: boolean }>;

export type MetaCountsProps = Omit<React.ComponentProps<"span">, "children"> & {
  /** `text` replaces the bare count when given ("2/5" of a checklist). */
  items: { icon: IconType; count: number; label: string; text?: string | undefined }[];
};

/** Icon + number pairs (comments, files, subtasks), 12 px muted; each names itself to readers. */
export function MetaCounts({ items, className, ...props }: MetaCountsProps) {
  return (
    <span
      data-slot="meta-counts"
      className={cn(
        "flex items-center gap-2.5 text-xs text-muted-foreground tabular-nums",
        className,
      )}
      {...props}
    >
      {items.map(({ icon: Icon, count, label, text }) => (
        <span key={label} className="inline-flex items-center gap-1">
          <Icon aria-hidden={true} className="size-3.5" />
          {text ?? count}
          <span className="sr-only">{label}</span>
        </span>
      ))}
    </span>
  );
}
